import React, { useState } from 'react'
import moment from 'moment';
import { PaymentMethod } from "@stripe/stripe-js";
// import { getMembershipPlans } from "../api/crud";
import CancelIcon from '@mui/icons-material/Cancel';
import { ResourceSchema, TrainingSchema, TrainingOrganizationSchema, ResourceType, ResourceBookingSchema  } from "../../data/types";
import { useCompleteSignIn, useLoadUserResourceBooking } from '../../hooks/authhooks'
import { useGetPaymentMethods } from '../../hooks/paymenthooks'
import { capitalizeString } from '../../utils/helpers';
// import StripePaymentCheckOut from "./PaymentCheckOut";
import BookingPaymentsView from "../../components/Mobile/BookingPaymentsView";
import { useNavigate } from 'react-router-dom';
import { ReactComponent as CheckMarkIcon } from "../../icons/CheckMarkIcon.svg";
import SubmitRequestButton from '../../components/SubmitRequestButton';
import FormProgressBar, { getActiveFormStep } from '../../components/FormProgressBar';



type RegisterTrainingFormProps = {
    resourceDetail: ResourceSchema
    handleClose: () => void;
    handleSubmitRequest: (resource: ResourceSchema, resourceType: ResourceType, booking: ResourceBookingSchema) => Promise<boolean> | boolean | void
};
  
const RegisterTrainingForm: React.FC<RegisterTrainingFormProps> = (props) => {
    var { resourceDetail, handleSubmitRequest, handleClose } = props;
    const resourceType = resourceDetail?.resource_type
    const navigate = useNavigate();
    const trainingOrganizations = (resourceDetail?.resource_inventory ?? {}) as TrainingOrganizationSchema

    const organizations: string[] = Object.keys(trainingOrganizations)
    const [trainingOrganization, setTrainingOrganization] = useState<string>(organizations[0]);

    var availableSchedules = trainingOrganizations[trainingOrganization]
    availableSchedules = availableSchedules ? availableSchedules?.filter(date => moment(date.date).isAfter(moment().startOf('day'))) : availableSchedules

    // const [application, setApplication] = useState<ResourceBookingSchema>({} as ResourceBookingSchema);
    const [selectedDate, setSelectedDate] = useState<string>("")
    const [timeOptions, setTimeOptions] = useState<string[]>([])
    const [selectedTime, setSelectedTime] = useState<string>("")

    const { user } = useCompleteSignIn();
    const loggedinUid = (user && user?.uid) ? user.uid : ''
    const { userResourceBooking } = useLoadUserResourceBooking(resourceDetail?.community_id || "", resourceDetail?.id || "", loggedinUid);
    var { paymentMethods } = useGetPaymentMethods(loggedinUid)

    paymentMethods = (paymentMethods && Array.isArray(paymentMethods)) ? paymentMethods : [];
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
      (paymentMethods && paymentMethods.length > 0) ? paymentMethods[0] : {} as PaymentMethod
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    const alreadySubmitted = Boolean(userResourceBooking?.status && userResourceBooking.status !== 'canceled')
    /** Registration steps: pick a date, pick a time, then pay when the training has a cost. */
    const registrationSteps = [
      Boolean(selectedDate),
      Boolean(selectedTime),
      ...(resourceDetail?.total_amount ? [Boolean(paymentMethod?.id)] : []),
    ]
    const totalSteps = registrationSteps.length
    const activeStep = alreadySubmitted ? totalSteps : getActiveFormStep(registrationSteps)

    const handleSetTraining = (e: React.ChangeEvent<HTMLSelectElement>) => {
      e.preventDefault()
      const {name, value} = e.target
      if (name === 'community_id' && value){
        setTrainingOrganization(value)
      } else if (name === 'date' && value){
        setSelectedDate(value)
        setTimeOptions(availableSchedules[parseInt(value)].times)
      } else if (name === 'payment_method' && value){
        const selectedMethod = paymentMethods.find((pm:any) => pm.id === value);
        setPaymentMethod(selectedMethod);
      }
    }
  
    const handleSubmit = async () => {
      if (isSubmitting || !(resourceType && selectedDate && selectedTime && availableSchedules.length > 0)) return;
      const application: ResourceBookingSchema = {
        start_time: `${availableSchedules[parseInt(selectedDate)].date} ${selectedTime}`,
        community_id: trainingOrganization,
        payment_method: paymentMethod?.id,
        total_hours: availableSchedules[0]?.duration ? availableSchedules[0].duration : 0,
        total_cost: resourceDetail.total_amount ? resourceDetail.total_amount : 0
      };
      setIsSubmitting(true);
      try {
        await handleSubmitRequest(resourceDetail, resourceType, application);
        handleClose();
        navigate('/bookings');
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <div className="flex flex-col justify-start items-center h-full min-h-0">
        <span onClick={handleClose} className="ml-auto w-full flex cursor-pointer mb-2">
            <CancelIcon className="text-[#A1A1A1] hover:text-[#777] h-[20px] w-[20px] ml-auto" />
        </span>

        <FormProgressBar totalSteps={totalSteps} currentStep={activeStep} variant="mobile" />
        
        {alreadySubmitted ? (
          <div className="flex flex-col items-center justify-center w-full py-8">
            <div className="w-16 h-16 mb-6 rounded-full bg-primaryDark-400 flex items-center justify-center">
              <CheckMarkIcon className="w-8 h-8 text-white" />
            </div>
            <h2 className='text-black font-semibold text-[20px] sm:text-[24px] font-raleway text-center mb-2'>
              Request Already Submitted
            </h2>
            <p className='text-[#4D4D4D] text-[14px] sm:text-[16px] font-open-sans text-center mb-6 max-w-md px-4'>
              You have already submitted a request for <span className='font-semibold'>{resourceDetail.name}</span> on <span className='font-semibold'>{moment(userResourceBooking?.created).format('MMM DD, YYYY')}</span>. Please check your bookings for status updates.
            </p>
            <button
              onClick={() => {
                handleClose();
                navigate('/bookings');
              }}
              className="py-3 px-8 font-semibold text-[16px] sm:text-[18px] font-raleway border-0 bg-primaryDark-0 text-white rounded-lg hover:bg-primaryDark-300 transition-colors touch-manipulation"
            >
              View My Bookings
            </button>
          </div>
        ) : (
          <>
            <p className='text-black font-semibold text-[20px] sm:text-[24px] font-raleway text-left w-full mb-1'>
              Register for <span className='font-bold'>{resourceDetail.name}</span>
            </p>
            <p className="text-left text-[14px] sm:text-[16px] my-2 font-open-sans font-normal text-[#4D4D4D] w-full mb-4">Fill out the details below to get certified.</p>

            <div className='border-[1px] border-[#E9E9E9] p-4 sm:p-6 rounded-lg w-full bg-white'>
          {/* <div className="flex flex-col mx-auto w-11/12 my-4">
              <label className='text-black font-semibold text-[16px] font-raleway text-left'>
                Pick a Training Organization
              </label>
              <div className='border-[#E9E9E9] border-[1px] rounded-md my-2'>
                  <select
                      className="formInput m-auto w-full h-[59px] mx-auto bg-inherit p-3 border-0"
                      name="community_id"
                      value={trainingOrganization}
                      onChange={handleSetTraining}
                  >
                    <option value="" disabled>
                        -- Select Training Organization --
                    </option>
                    {
                      organizations.map((data: string, i: number)=>
                        (
                          <option value={data} key={i}>
                              {data ? 'Default' : capitalizeString(data)}
                          </option>
                        )
                      )
                    }
                  </select>
              </div>
          </div> */}

          <div className="flex flex-col w-full mb-4">
          {
            (availableSchedules && availableSchedules.length > 0) ? (
              <>
                <label className='text-black font-semibold text-[15px] sm:text-[16px] font-raleway text-left mb-2'>
                  Available Dates
                </label>
                <div className='border-[#E9E9E9] border-[1px] rounded-lg'>
                    <select
                        className="formInput w-full h-[52px] sm:h-[59px] bg-inherit px-4 py-3 border-0 text-[15px] sm:text-[16px] font-open-sans text-[#333333]"
                        name="date"
                        value={selectedDate}
                        onChange={handleSetTraining}
                    >
                      <option value="">
                          -- Select Date --
                      </option>
                      {
                        availableSchedules.map((date: TrainingSchema, i: number)=>
                        (
                          <option value={i} key={i}>
                              {moment(date.date).format('ddd MMM DD, YYYY')}
                          </option>
                        ))
                      }
                    </select>
                </div>
              </>
            ) : (<p className='text-[#A1A1A1] text-[14px] italic font-open-sans'>No Dates Available</p>)
          }

          </div>

          <div className="flex flex-col w-full mb-4">
            {
              ((availableSchedules && availableSchedules.length > 0) && selectedDate) ? (
              <>
                <label className='text-black font-semibold text-[15px] sm:text-[16px] font-raleway text-left mb-2'>
                  Available Time
                </label>
                <div className='flex flex-row flex-wrap gap-2'>
                    {
                       (timeOptions && timeOptions.length > 0) ? timeOptions.map((time, i)=> {
                        return (
                          <button
                            key={i} 
                            type="button"
                            className={`${(selectedTime === time) ? 'bg-primaryDark-400 text-white border-primaryDark-400' : 'text-black bg-white border-[#E9E9E9]'} cursor-pointer min-w-[80px] flex-1 sm:flex-none rounded-lg text-center border-[1px] font-open-sans text-[14px] sm:text-[16px] px-3 py-2.5 font-medium transition-colors touch-manipulation`}
                            onClick={()=> {
                              selectedTime === time ? setSelectedTime("") : setSelectedTime(time)
                            }}
                          >
                            {moment(time, 'HH:mm').format('hh:mm a')}
                          </button>
                        )
                        }) : (<p className='text-[#A1A1A1] text-[14px] italic font-open-sans w-full'>No Times Available</p>)
                    }    
                </div>
              </> ) : (<></>)
            }
          </div>
          {
            (resourceDetail?.total_amount) ? (
              <div className="flex flex-col w-full mb-4">
                  <label className='text-black font-semibold text-[15px] sm:text-[16px] font-raleway text-left mb-2'>
                    Payment Method
                  </label>

                  {(paymentMethods && paymentMethods.length > 0) && (<BookingPaymentsView paymentMethod={paymentMethod} handleSetApplication={handleSetTraining} />)}
              </div>
            ) : (<></>)
          }

        
          <div className="flex flex-row items-center justify-between rounded-lg h-auto w-full my-4 pt-4 border-t border-[#E9E9E9] text-black font-semibold text-[15px] sm:text-[16px] font-raleway">
            <p>{resourceType ? capitalizeString(resourceType) : 'Resource'} cost:</p>
            <p className='font-semibold ml-2'>${resourceDetail?.total_amount ? resourceDetail.total_amount.toFixed(2) : '0.00'}</p>
          </div>
    

        </div>
        <div className='flex flex-row gap-3 my-4 w-full'>

          <button
              className="flex-1 py-3 px-4 font-semibold text-[16px] sm:text-[18px] font-raleway text-primaryDark-0 bg-white border-[#E9E9E9] border-[1px] rounded-lg hover:bg-gray-50 transition-colors touch-manipulation"
              data-testid="cancel-button"
              onClick={handleClose}
            > 
                  Cancel  
          </button>
          <SubmitRequestButton
            isSubmitting={isSubmitting}
            disabled={!selectedTime || (!paymentMethod?.id && resourceDetail?.total_amount ? true : false)}
            onClick={handleSubmit}
            className={`flex-1 py-3 px-4 font-semibold text-[16px] sm:text-[18px] font-raleway border-0 rounded-lg transition-colors touch-manipulation text-white ${(!selectedTime || (!paymentMethod?.id && resourceDetail?.total_amount ? true : false)) ? 'bg-gray-300 cursor-not-allowed' : 'bg-primaryDark-0 hover:bg-primaryDark-300'}`}
          />
        </div>
          </>
        )}
   
      </div>
    );
};
 

export default RegisterTrainingForm